/**
 * TemplateTypeSwitcher - which kind of template you are making, and how to
 * make a different one.
 *
 * Every template library shares one route and one creation screen, so the
 * three forms looked alike: the same glass card, the same Name field, the same
 * Create button. Nothing on the page said whether the thing about to be
 * created was a document structure, a set of table columns or a playbook —
 * you had to remember which tab you pressed to get here.
 *
 * So the form opens by saying it. The type is the first thing in the body,
 * with its icon and a line on what one of these actually is.
 *
 * And it is CHANGEABLE. Picking the wrong library is an easy mistake and the
 * old cost of it was leaving the form - back to the list, find the right tab,
 * press Add again. The type reads as what it is: a control, showing a value,
 * that opens the other choices.
 *
 * Switching is a `replace`, not a `push`. The forms are mounted from
 * `?type=`, so changing the parameter swaps the form in place - and `replace`
 * keeps Back pointing at the list you came from rather than walking you
 * through every type you looked at on the way.
 */
import React from 'react';
import clsx from 'clsx';
import { Menu } from '@headlessui/react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '../shared/TailwindComponents/Button';
import {
  PiBookOpenTextLight,
  PiCaretDownLight,
  PiCheckLight,
  PiListLight,
  PiPaletteLight,
  PiTableLight,
} from 'react-icons/pi';
import {
  TEMPLATE_LIBRARIES,
  TemplateLibrary,
  TemplateLibraryId,
  templateCreatePath,
} from './templateLibraries';

const ICONS: Record<TemplateLibrary['icon'], React.ComponentType<{ className?: string }>> = {
  PiListLight,
  PiTableLight,
  PiBookOpenTextLight,
  PiPaletteLight,
};

export interface TemplateTypeSwitcherProps {
  current: TemplateLibraryId;
  /**
   * Switching leaves whatever has been typed behind, so a form with unsaved
   * input asks first. Omitted → the switch is immediate.
   */
  onBeforeSwitch?: (go: () => void) => void;
  /**
   * `card` (default) - the glass lead-in card naming the type.
   * `header` - the form's OWN header: title and description on the left, the
   * "Change type" control at the top-right beside them. The form passes
   * `hideHeader` to its shell so there is one header, not two.
   */
  variant?: 'card' | 'header';
  /** `header` variant: the form title. Defaults to the library name. */
  title?: React.ReactNode;
  /** `header` variant: the line under the title. Defaults to the library description. */
  description?: React.ReactNode;
}

const TemplateTypeSwitcher: React.FC<TemplateTypeSwitcherProps> = ({
  current,
  onBeforeSwitch,
  variant = 'card',
  title,
  description,
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  const active = TEMPLATE_LIBRARIES.find((lib) => lib.id === current);
  if (!active) return null;

  const ActiveIcon = ICONS[active.icon];

  const switchTo = (id: TemplateLibraryId) => {
    if (id === current) return;
    const go = () => history.replace(templateCreatePath(id));
    if (onBeforeSwitch) onBeforeSwitch(go);
    else go();
  };

  const menu = (
      <Menu as="div" className="relative flex-shrink-0">
        <Menu.Button
          as={Button}
          type="button"
          variant="neutral"
          size="small"
          data-testid="template-type-switcher"
        >
          <span className="flex items-center gap-1.5">
            {t('Change type')}
            <PiCaretDownLight className="h-4 w-4 text-fg-muted" />
          </span>
        </Menu.Button>

        {/* Opaque and portalled, like every other menu in the app - see
            `DocumentPicker` for why `anchor` rather than `absolute`. */}
        <Menu.Items
          anchor="bottom end"
          transition
          className="[--anchor-gap:6px] [--anchor-padding:16px] z-[300] w-[320px] max-w-[92vw]
                     origin-top rounded-xl bg-surface p-1.5 shadow-xl ring-1 ring-black/[0.05]
                     transition duration-150 ease-pop focus:outline-none
                     data-[closed]:scale-95 data-[closed]:opacity-0 dark:ring-white/[0.08]"
        >
          {TEMPLATE_LIBRARIES.map((lib) => {
            const Icon = ICONS[lib.icon];
            const isCurrent = lib.id === current;
            return (
              <Menu.Item key={lib.id}>
                {({ active: focused }) => (
                  <button
                    type="button"
                    onClick={() => switchTo(lib.id)}
                    className={clsx(
                      'flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-start transition-colors',
                      focused && !isCurrent && 'bg-surface-soft',
                      isCurrent && 'bg-champagne/10',
                    )}
                  >
                    <Icon
                      className={clsx(
                        'mt-0.5 h-4 w-4 flex-shrink-0',
                        isCurrent ? 'text-champagne' : 'text-fg-muted',
                      )}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-fg-strong">{t(lib.label)}</span>
                      <span className="mt-0.5 block text-xs leading-relaxed text-fg-muted">
                        {t(lib.description)}
                      </span>
                    </span>
                    {isCurrent && (
                      <PiCheckLight className="mt-0.5 h-4 w-4 flex-shrink-0 text-champagne" />
                    )}
                  </button>
                )}
              </Menu.Item>
            );
          })}
        </Menu.Items>
      </Menu>
  );

  if (variant === 'header') {
    return (
      <div className="flex items-start gap-4 border-b border-hairline pb-4">
        <span
          aria-hidden
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-hairline-soft bg-champagne/10"
        >
          <ActiveIcon className="h-5 w-5 text-champagne" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-lg font-medium text-fg-strong">{title ?? t(active.label)}</h2>
          <p className="mt-1.5 text-sm text-fg-muted">{description ?? t(active.description)}</p>
        </div>
        {menu}
      </div>
    );
  }

  return (
    <div className="glass flex flex-wrap items-center gap-4 rounded-lg p-4 shadow-sm">
      {/* The mark. Champagne is the accent, so it is a small tinted disc —
          never a large fill. */}
      <span
        aria-hidden
        className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg border border-champagne/30 bg-champagne/10"
      >
        <ActiveIcon className="h-5 w-5 text-champagne" />
      </span>

      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium text-fg-muted">
          {t('New template')}
        </p>
        <h3 className="truncate text-base text-fg-strong">{t(active.label)}</h3>
        <p className="mt-0.5 text-sm text-fg-muted">{t(active.description)}</p>
      </div>

      {menu}
    </div>
  );
};

export default TemplateTypeSwitcher;
